import {ColorPicker} from "primereact/colorpicker";
import {Column, ColumnBodyOptions, ColumnEditorOptions} from "primereact/column";
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import React from 'react';
import { DatasetChartProps, ILineConfig } from '../../interfaces';

const DASH_OPTIONS = [
    { label: 'Solid', value: 'solid', strokeDasharray: '0' },
    { label: 'Dotted', value: 'dot', strokeDasharray: '2' },
    { label: 'Dashed', value: 'dash', strokeDasharray: '5' },
    { label: 'Long Dashed', value: 'longdash', strokeDasharray: '10' },
    { label: 'Dash Dot', value: 'dashdot', strokeDasharray: '15, 3, 3, 3' },
    { label: 'Long Dash Dot', value: 'longdashdot', strokeDasharray: '25, 3, 3, 3' },
];

type LineEditorProps = {
    onConfigChange: (config: DatasetChartProps) => void;
    setCurrentConfig: (config: DatasetChartProps) => void;
    currentConfig: DatasetChartProps;
};

type DashOption = {
    label: string;
    value: string;
    strokeDasharray: string;
};

function LineEditor({ setCurrentConfig, currentConfig }: LineEditorProps) {
    const customDropdownItem = (option: DashOption) => {
        return option ? (
            <div>
                <svg height='15' width='100'>
                    <line
                        x1='0'
                        y1='50%'
                        x2='100%'
                        y2='50%'
                        stroke='black'
                        strokeWidth='2'
                        strokeDasharray={option.strokeDasharray}
                    />
                </svg>
            </div>
        ) : (
            <div>Select a dash</div>
        );
    };

    const handleEditorCallback = (options: any, newLine: ILineConfig) => {
        if (!currentConfig.datasets) {
            return;
        }
        const identifier = options.rowData.identifier;
        const datasetIndex = currentConfig.datasets.findIndex(dataset => dataset.identifier === identifier);
        if (datasetIndex !== -1) {
            const updatedLine = { ...currentConfig.datasets[datasetIndex].line, ...newLine };
            const updatedDatasets = [...currentConfig.datasets];
            updatedDatasets[datasetIndex] = { ...updatedDatasets[datasetIndex], line: updatedLine };
            setCurrentConfig({ ...currentConfig, datasets: updatedDatasets });
            options.editorCallback!(updatedLine);
            options.rowData.line.color = newLine.color;
        }
    };

    const handleRangeEditorCallback = (options: ColumnEditorOptions, newRange: Array<number>) => {
        if (!currentConfig.datasets) {
            return;
        }
        const identifier = options.rowData.identifier;
        const datasetIndex = currentConfig.datasets.findIndex(dataset => dataset.identifier === identifier);
        if (datasetIndex !== -1) {
            const updatedDatasets = [...currentConfig.datasets];
            updatedDatasets[datasetIndex] = { ...updatedDatasets[datasetIndex], range: newRange };
            setCurrentConfig({ ...currentConfig, datasets: updatedDatasets });
            options.editorCallback!(newRange);
        }
    };

    const lineColorEditor = (options: ColumnEditorOptions) => {
        return (
            <>
                <ColorPicker
                    value={options.rowData.line?.color ?? options.value?.color}
                    defaultColor={"#FFFFFF"}
                    onChange={(e) => handleEditorCallback(options, {color: e.target.value as string})}
                />
                <i
                    className="pi pi-refresh ml-2 cursor-pointer"
                    onClick={() =>
                        handleEditorCallback(options, {color: undefined})
                    }
                />
            </>
    )
        ;
    };

    const colorBodyTemplate = (rowData: any) => {
        const currentColor = currentConfig.datasets
            ? currentConfig.datasets.find((dataset) => dataset.identifier === rowData.identifier)?.line?.color
            : undefined;
        return (
            <div
                style={{
                    width: '25px',
                    height: '25px',
                    backgroundColor: '#' + currentColor,
                    borderRadius: '5px',
                    border: '1px solid #c8c8c8',
                }}
            ></div>
        );
    };

    const numberEditor = (options: ColumnEditorOptions) => {
        return (
            <InputNumber
                inputStyle={{ width: '100%', height: '100%'}}
                value={options.rowData.line?.width}
                onValueChange={(e) => handleEditorCallback(options, { width: e.target.value })}
            />
        );
    };

    const widthBodyTemplate = (rowData: any) => {
        const currentWidth = currentConfig.datasets
            ? currentConfig.datasets.find((dataset) => dataset.identifier === rowData.identifier)?.line?.width
            : undefined;
        return <div style={{ width: '100%', height: '100%', textAlign: 'center' }}>{currentWidth}</div>;
    };

    const dashEditor = (options: ColumnEditorOptions) => {
        return (
            <Dropdown
                className='w-full'
                value={options.value?.dash ?? options.rowData.line?.dash}
                placeholder='Select a dash'
                options={DASH_OPTIONS}
                onChange={(e) => handleEditorCallback(options, { dash: e.value })}
                itemTemplate={customDropdownItem}
                valueTemplate={customDropdownItem}
            />
        );
    };

    const dashBodyTemplate = (rowData: any) => {
        const currentDash = currentConfig.datasets
            ? currentConfig.datasets.find((dataset) => dataset.identifier === rowData.identifier)?.line?.dash
            : undefined;
        const dashArray = DASH_OPTIONS.find((option) => option.value === currentDash)?.strokeDasharray
        return (
            <div>
                <svg height="15" width="100">
                    <line
                        x1="0"
                        y1="50%"
                        x2="100%"
                        y2="50%"
                        stroke="black"
                        strokeWidth="2"
                        strokeDasharray={dashArray}
                    />
                </svg>
            </div>
        );
    };

    const rangeEditor = (options: ColumnEditorOptions) => {
        return (
            <div className="flex flex-row">
                <InputNumber
                    inputStyle={{ width: '100%', height: '100%'}}
                    value={options.rowData.range ? options.rowData.range[0] : undefined}
                    placeholder="MIN"
                    onChange={(e) => handleRangeEditorCallback(options, [e.value, options.rowData.range ? options.rowData.range[1] : undefined])}
                />
                <InputNumber
                    inputStyle={{ width: '100%', height: '100%'}}
                    value={options.rowData.range ? options.rowData.range[1] : undefined}
                    placeholder='MAX'
                    onChange={(e) => handleRangeEditorCallback(options, [options.rowData.range ? options.rowData.range[0] : undefined, e.value])}
                />
            </div>
        );
    };

    const rangeBodyTemplate = (rowData: any) => {
        const currentRange =
            currentConfig.datasets &&
            currentConfig.datasets.find((dataset) => dataset.identifier === rowData.identifier)?.range;

        const isRangeValid = currentRange && currentRange.every(value => value !== undefined && value !== null);
        return (
            <div>
                {isRangeValid ? `[ ${currentRange[0]} : ${currentRange[1]} ]` : 'No valid range'}
            </div>
        );
    };

    const onRowEditComplete = (e: any) => {
        let datasetsToUpdate = [...currentConfig.datasets!];
        let { newData, index } = e;

        delete newData.index;
        datasetsToUpdate[index] = e.data;
        setCurrentConfig({ ...currentConfig, datasets: datasetsToUpdate });
    };

    return (
        <DataTable value={currentConfig.datasets} onRowEditComplete={onRowEditComplete} editMode='row'>
            <Column field='identifier' header='Identifier' style={{ width: '30%', padding: 0 }} />
            <Column
                field='color'
                header='Color'
                style={{ width: '10%', padding: 0 }}
                editor={(options) => lineColorEditor(options)}
                body={colorBodyTemplate}
            />
            <Column
                field='width'
                header='Width'
                style={{ width: '5%', padding: 0 }}
                editor={(options) => numberEditor(options)}
                body={widthBodyTemplate}
            />
            <Column
                field='dash'
                header='Dash'
                style={{ width: '20%', padding: 0 }}
                editor={(options) => dashEditor(options)}
                body={dashBodyTemplate}
            />
            <Column
                field='range'
                header='Y Axis Range'
                style={{ width: '20%', padding: 0 }}
                editor={(options) => rangeEditor(options)}
                body={rangeBodyTemplate}
            />
            <Column rowEditor align='center' style={{ width: '10%' }} />
        </DataTable>
    );
}

export default LineEditor;
